import { useState, useEffect, useRef, useCallback } from 'react';
import { AgGridReact } from "ag-grid-react";
import { useTranslation, withTranslation } from 'react-i18next';
import { cloneDeep, isEmpty, isEqual, isNull, isUndefined } from 'lodash';

import { Box, Button, CardMedia, styled, ToggleButtonGroup, Typography } from '@mui/material';
import AppCard from "@plannel/components/ui/AppCard";
import { green, grey, red } from '@mui/material/colors';
import { StyledToggleButton } from "@plannel/components/ui/ToggleButton";
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { SwiperSlide } from 'swiper/react';
import '@plannel/pages/demand-plan/demand-management/swiper-scss/swiper.scss';
import '@plannel/pages/demand-plan/demand-management/swiper-scss/swiper-navigation.scss'
import comparisonComponent from "@plannel/components/layout/ComparisonContainer";
import { ComparisonReleaseBar, SelectableComparison } from "@plannel/components/layout/ComparisonSelection";

import DefaultGridSetting from '@plannel/components/aggrid/DefaultGridSetting';
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import GridUtils from '@plannel/components/aggrid/GridUtils';
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import Snackbar from '@plannel/components/Snackbar';
import { LockButton, UnlockButton } from "@plannel/components/ActionIconButton";
import ScenarioReleaseModal from "@plannel/components/modal/ScenarioReleaseModal";
import UnconfirmedScenarioNotice, { isUnconfirmedScenarioVersion } from "@plannel/components/UnconfirmedScenarioNotice";

import ipScenarioCompService from '@plannel/services/ip/ip-scenario-comp-service';
import planVersionService from "@plannel/services/data/plan-version-service";

import debug from "@plannel/utils/debug-util";
import numberUtil from '@plannel/utils/number-util';
import CodeUtil from '@plannel/pages/utils/code-util';
import dateUtils from '@plannel/utils/date-util';
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

const { RELEASED, RELEASING } = CodeUtil.ipVersionStatusCode;
const CustomHeader = (props) => {
  const { displayName } = props;
  const NoPaddingButton = styled(Button)(() => ({
    "&.MuiButtonBase-root": {
      padding: "0px"
    }
  }));

  const onClickedHeader = () => {
    if(props.onClickHandler) {
      props.onClickHandler();
    }
  }

  return (
    <Box sx={{ display: "flex", alignItems: "left" }}>
      <NoPaddingButton onClick={onClickedHeader}>{displayName}</NoPaddingButton>
    </Box>
  );
}

const ABC = "abc";
const XYZ = "xyz";
// "ABC-XYZ" 요약 셀 = partial chip: 코드(AX~CZ)만 공용 틴트 칩(ColorUtil.tintAbcXyzChipSx),
// 뒤의 "(N ITEM)" 은 평문. total 등 비-ABC 행은 값 전체를 평문으로.
/*
 * ABC-XYZ 칸의 라벨은 코드·개수에서 그때그때 만든다.
 * 번역문을 행 데이터에 담아 두면 언어를 바꿔도 state 가 그대로라
 * 새로 조회할 때(=새로고침)까지 옛 언어가 남는다.
 * 그리드 콜백은 setColumnDefs 시점의 t 를 가둬 두므로, 호출 시점에 언어 번들을 읽는
 * gridValueL10N 을 쓴다(언어가 바뀌면 relocalizeAllGrids 가 셀을 다시 그려 준다).
 */
const TOTAL_CD = "total";

const abcXyzFormatter = params => {
  const code = params?.data?.classCd;
  if (isNull(code) || isUndefined(code)) {
    return "";
  }
  const label = code === TOTAL_CD ? GridUtils.gridValueL10N(TOTAL_CD) : String(code);
  const count = params?.data?.classCnt;
  return isNull(count) || isUndefined(count)
    ? label
    : `${label} (${count} ${GridUtils.gridValueL10N("item")})`;
};

const AbcXyzLabelCell = ({ valueFormatted, value, data }) => {
  const text = valueFormatted ?? (value == null ? "" : String(value));
  const code = String(data?.classCd ?? "").toUpperCase();
  const sx = ColorUtil.tintAbcXyzChipSx(code);
  if (!sx) {
    return text;
  }
  const rest = text.replace(/^\s*\S+\s*/, "").trim();   // 앞 코드 토큰 제거 → "(26 ITEM)"
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
      <Box component="span" sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: 30, px: 0.75, py: "1px", borderRadius: 999,
        fontSize: FS.BODY, fontWeight: 700, lineHeight: 1.3, flexShrink: 0, ...sx,
      }}>
        {code}
      </Box>
      {rest && <Box component="span" sx={{ fontSize: FS.SUB, color: "text.primary" }}>{rest}</Box>}
    </Box>
  );
};

const ISPComparisonDashboard = ({ t, title }) => {
  const [scenarioDatas, setScenarioDatas] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [dataMax, setDataMax] = useState("auto");
  // use only when released version api called
  const [isReleasing, setIsReleasing] = useState(false);
  const dialogContent = { title: "kitTargetInit", content: t("MSG_InitKitTargetStock") };
  const selectedVersion = useRef();
  const { snackBarMessageColor } = ColorUtil;

  const handleReleaseOpen = () => {
    setReleaseOpen(true);
  };

  // 카드에서 고른 시나리오 — 릴리즈는 상단 공통 바에서 이 시나리오 하나로만 실행한다.
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  const selectedScenario = scenarioDatas?.find(scenarioData => isEqual(scenarioData?.scenarioId, selectedScenarioId)) ?? null;
  const selectedPlanVersion = selectedScenario?.planVersion;
  const releasable = !!selectedScenario
    && !isEqual(selectedPlanVersion?.statusCd, RELEASED)
    && !isEqual(selectedPlanVersion?.statusCd, RELEASING)
    && !isUnconfirmedScenarioVersion(selectedPlanVersion);

  // 버튼이 회색인 이유를 툴팁으로 알려 준다.
  const releaseBlockReason = !selectedScenario || releasable
    ? null
    : isUnconfirmedScenarioVersion(selectedPlanVersion)
      ? t("MSG_ScenarioPlanningNotConfirmed")
      : t("MSG_NoVersionToRelease");

  const handleReleaseClick = () => {
    if (!selectedScenario) {
      return;
    }
    selectedVersion.current = {
      scenarioId: selectedScenario.scenarioId,
      name: selectedScenario.scenarioName,
      kitTargetInit: selectedScenario.kitTargetInit,
    };
    handleReleaseOpen();
  };

  const getMaxValue = (arrayDatas) => {
    const allDatas = [];
    for (const arrayData of arrayDatas) {
      let aCnt = 0;
      let bCnt = 0;
      let cCnt = 0;
      for (const resultData of arrayData?.scenarioResultList) {
        if (resultData?.invAmt) {
          if (isEqual(resultData.abcGroup, "A")) {
            aCnt += resultData.invAmt;
          } else if (isEqual(resultData.abcGroup, "B")){
            bCnt += resultData.invAmt;
          } else if (isEqual(resultData.abcGroup, "C")){
            cCnt += resultData.invAmt;
          }
        }
      }
      allDatas.push(aCnt, bCnt, cCnt);
    }
    const maxValue = numberUtil.getMaxValueForChart(allDatas);
    setDataMax(maxValue);
  }

  const retrieveIspScenario = () => {
    ipScenarioCompService.getAllScenarios()
      .then(response => {
        const retrieveScenarioDatas = response?.data ? response.data : [];

        if (!isEmpty(retrieveScenarioDatas)) {
          const hasReleasingVersion = retrieveScenarioDatas.some(
            scenario => isEqual(scenario.planVersion.statusCd, RELEASING)
          );
          setIsReleasing(hasReleasingVersion);

          getMaxValue(retrieveScenarioDatas);
          for (const scenarioData of retrieveScenarioDatas) {
            const totalData = {
              /* 보이는 글자는 abcXyzFormatter 가 만든다 — 여기엔 코드만 담는다. */
              abcXyz: TOTAL_CD,
              classCd: TOTAL_CD,
              classCnt: scenarioData.totalClassCnt,
              invAmt: scenarioData.totalInvAmt,
              rate: 0,
            };

            const arrayData = scenarioData?.scenarioResultList ? scenarioData.scenarioResultList : [];
            for (const data of arrayData) {
              data.abcXyz = data?.classCd;
              totalData.rate = totalData.rate + data.rate; // == 100%
            }
            totalData.rate = 1; // normalize to exactly 1.0 to avoid float overflow on sparkline max

            arrayData.unshift(totalData);
          }

          retrieveScenarioDatas.sort((a, b) => {
            if (a.planVersion?.statusCd !== b.planVersion?.statusCd) {
              return b.planVersion?.statusCd.localeCompare(a.planVersion?.statusCd);
            } else {
              if (a.applyFlg) {
                return -1;
              } else {
                return 1;
              }
            }
          });

          setScenarioDatas(retrieveScenarioDatas);
        } else {
          setScenarioDatas([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoVersion") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_NetworkError") });
        setScenarioDatas([]);
        debug(e);
      });
  }

  const updateVersionDelFlg = planVersion => {
    const planVersionId = { planVersionId: planVersion.id };
    planVersionService.delFlg(planVersionId)
      .then(response => {
        const updatedVersion = response.data;

        const datas = [];
        for (const data of scenarioDatas) {
          if (isEqual(updatedVersion.id, data.planVersion.id)) {
            data.planVersion = updatedVersion;
          }
          datas.push(data);
        }

        setScenarioDatas(datas);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t(e.response.data) });
        debug(e);
      });
  }

  useEffect(() => {
    retrieveIspScenario();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (<>
    <Box className={"wrap-comparion-dashboard"}>
      <Box sx={{ width: "100%", flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
        {!isEmpty(scenarioDatas) ?
         <>
         <ComparisonReleaseBar
           selectedName={selectedScenario?.scenarioName}
           disabled={!releasable}
           disabledReason={releaseBlockReason}
           isReleasing={isReleasing}
           onRelease={handleReleaseClick}
         />
         <comparisonComponent.ComparisonContainer>
          {
            scenarioDatas?.map((scenarioData, idx) => (
              <SwiperSlide key={scenarioData.scenarioId}>
                <SelectableComparison
                  selected={isEqual(scenarioData.scenarioId, selectedScenarioId)}
                  onSelect={() => setSelectedScenarioId(prev => (isEqual(prev, scenarioData.scenarioId) ? null : scenarioData.scenarioId))}>
                  <ComparisonContentComponent
                    key={`${scenarioData.scenarioId}-content`}
                    title={title}
                    dataMax={dataMax}
                    scenarioData={scenarioData}
                    updateVersionDelFlg={updateVersionDelFlg}
                    isReleasing={isReleasing}
                    index={idx}
                  />
                </SelectableComparison>
              </SwiperSlide>
              ))
          }
         </comparisonComponent.ComparisonContainer>
         </>
          : <Box sx={{ width: "30%" }}>
            <ComparisonContentComponent
              title={title}
              scenarioData={scenarioDatas}
              updateVersionDelFlg={updateVersionDelFlg}
              index={0}
            />
          </Box>}
      </Box>
    </Box>
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    <ScenarioReleaseModal
      open={releaseOpen}
      close={setReleaseOpen}
      selectScenario={selectedVersion?.current ?? {}}
      service={ipScenarioCompService}
      moduleCd={CodeUtil.module.IP}
      setIsReleasing={setIsReleasing}
      reload={retrieveIspScenario}
      dialogContent={selectedVersion?.current?.kitTargetInit ? dialogContent : {}}
    />
  </>)
};

/*
  Chart, Grid Component
*/
const HEADER_CODES = ["ABC-XYZ","ABC","XYZ"];
const ABC_ARRAY = ["A","B","C"];
const XYZ_ARRAY = ["X","Y","Z"];
const ComparisonContentComponent = ({ title, scenarioData, updateVersionDelFlg, index, dataMax, isReleasing }) => {
  const { t } = useTranslation();
  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = userData?.currencySymbol ?? "";
  const [scenarioResultDataOrigin] = useState(scenarioData?.scenarioResultList ? scenarioData.scenarioResultList : []);
  const totalInvAmt = scenarioData?.totalInvAmt;
  const totalHoldingCost = scenarioData?.holdingCost;
  const titleName = scenarioData.scenarioName;
  const planVersion = scenarioData?.planVersion;
  const abcResultData = useRef([]);
  const xyzResultData = useRef([]);

  const [gridResultData, setGridResultData] = useState(scenarioResultDataOrigin);
  const [headerCodeNumber, setHeaderCodeNumber] = useState(0);

  const [chartTypeToggle, setChartTypeToggle] = useState(ABC);

  const setHeaderMultipleData = (classArray, headerCode, makeObject, gridData) => {
    for (const classEl of classArray) {
      let compareValue = gridData?.abcGroup;
      if (headerCode === HEADER_CODES[2]) {
        compareValue = gridData?.xyzGroup;
      }

      if (compareValue === classEl) {
        makeObject.total.invAmt += gridData.invAmt;
        makeObject.total.classCnt += gridData.classCnt;

        if (makeObject?.[classEl] && makeObject[classEl] !== 0) {
          makeObject[classEl].invAmt += gridData.invAmt;
          makeObject[classEl].classCnt += gridData.classCnt;
        } else {
          makeObject[classEl] = {
            invAmt: gridData.invAmt,
            abcXyz: classEl,
            classCd: classEl,
            classCnt: gridData.classCnt,
            rate: 0
          }
        }
      }
    }
  }

  const settingGridDatas = (datas) => {
    const GridDataList = cloneDeep(datas);

    if (!isEmpty(GridDataList)) {
      /* 합계 행도 코드(classCd)로 식별한다 — 라벨은 abcXyzFormatter 가 만든다. */
      const abcObject = {total: {invAmt: 0, abcXyz: TOTAL_CD, classCd: TOTAL_CD, rate:0, classCnt: 0}};
      const xyzObject = {total: {invAmt: 0, abcXyz: TOTAL_CD, classCd: TOTAL_CD, rate:0, classCnt: 0}};

      for(const gridData of GridDataList) {
        // gridData Setting when header code equals ABC
        setHeaderMultipleData(ABC_ARRAY, HEADER_CODES[1], abcObject, gridData);

        // gridData Setting when header code equals XYZ
        setHeaderMultipleData(XYZ_ARRAY, HEADER_CODES[2], xyzObject, gridData);

      }

      if(abcObject.total.invAmt > 0) {
        abcObject.A.rate = abcObject.A.invAmt / abcObject.total.invAmt;
        abcObject.B.rate = abcObject.B.invAmt / abcObject.total.invAmt;
        abcObject.C.rate = abcObject.C.invAmt / abcObject.total.invAmt;
        abcObject.total.rate = 1;
      }

      if(xyzObject.total.invAmt > 0) {
        xyzObject.X.rate = xyzObject.X.invAmt / xyzObject.total.invAmt;
        xyzObject.Y.rate = xyzObject.Y.invAmt / xyzObject.total.invAmt;
        xyzObject.Z.rate = xyzObject.Z.invAmt / xyzObject.total.invAmt;
        xyzObject.total.rate = 1;
      }

      abcResultData.current = [];
      abcResultData.current.push(abcObject.total);
      abcResultData.current.push(abcObject.A);
      abcResultData.current.push(abcObject.B);
      abcResultData.current.push(abcObject.C);

      xyzResultData.current = [];
      xyzResultData.current.push(xyzObject.total);
      xyzResultData.current.push(xyzObject.X);
      xyzResultData.current.push(xyzObject.Y);
      xyzResultData.current.push(xyzObject.Z);
    }
  }

  const sortChartData = (sortDatas) => {
    if (chartTypeToggle === ABC) {
      sortDatas.sort((a, b) => {
        if (a.abcGroup < b.abcGroup) return -1;
        if (a.abcGroup > b.abcGroup) return 1;
        return 0;
      });
    } else {
      sortDatas.sort((a, b) => {
        if (a.xyzGroup < b.xyzGroup) return -1;
        if (a.xyzGroup > b.xyzGroup) return 1;
        return 0;
      });
    }
  }

  const settingChartDatas = (datas) => {
    const chartData = [];

    if (isEmpty(datas)) {
      return chartData;
    }

    let chartDataObj = {};
    const sortDatas = datas?.scenarioResultList ? datas.scenarioResultList : [];

    sortChartData(sortDatas);

    for (const data of sortDatas) {
      const { classCd, invAmt, abcGroup, xyzGroup } = data;

      if (classCd === "total") {
        continue;
      }

      let classGroup = "";
      if (chartTypeToggle === ABC) {
        classGroup = abcGroup;
      } else {
        classGroup = xyzGroup;
      }

      if (isEmpty(chartDataObj) || chartDataObj.classGroup !== classGroup) {
        if (!isEmpty(chartDataObj)) {
          chartData.push(chartDataObj);
        }

        chartDataObj = {
          classGroup,
        };
      }

      chartDataObj.classCd = classGroup;
      chartDataObj[classCd] = invAmt;
    }

    if (!isEmpty(chartDataObj)) {
      chartData.push(chartDataObj);
    }

    return chartData;
  };

  const chartDatas = settingChartDatas(scenarioData);

  const ratioValueGetter = ({ data }) => {
    const { rate } = data;
    return rate * 100;
  }

  const ratioLabelPercentFormatter = (params) => {
    const value = numberUtil.formatNumberData(params, { maximumFractionDigits: 1 });

    return `${value}%`;
  }

  const setCurrency = (value) => {
    if (value) {
      const number = Number(value);
      if (number > 0) {
        return symbol + numberUtil.formatNumber(number);
      }
      return `- ${symbol}${numberUtil.formatNumber(Math.abs(number))}`;
    }
    return null;
  }

  const numberFormatter = ({ data }) => {
    const value = data.invAmt;

    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatNumber = numberUtil.formatNumber(value);
    return symbol + formatNumber;
  }

  const headerOnClickHandler = () => {
    if(headerCodeNumber !== 0 && headerCodeNumber % 2 === 0) {
      setHeaderCodeNumber(0);
    } else {
      setHeaderCodeNumber(codeNumber => codeNumber + 1);
    }
  }

  const gridDefs = [
    {
      headerName: "ABC-XYZ",
      field: "abcXyz",
      width: 130,
      headerComponent: CustomHeader,
      headerComponentParams: { onClickHandler: headerOnClickHandler, displayName: HEADER_CODES[headerCodeNumber] },
      valueFormatter: abcXyzFormatter,
      cellRenderer: AbcXyzLabelCell
    },
    {
      headerName: " ", field: "rate", valueGetter: ratioValueGetter,
      cellRenderer: RatioBarCell,
      cellRendererParams: { formatter: ratioLabelPercentFormatter }
    },
    {
      headerName: "invAmt",
      field: "invAmt",
      type: ["numberColumn", "nonEditableColumn", "rightAligned"],
      valueFormatter: numberFormatter
    }
  ];

  // 세로 배치를 flex 로 둬서 그리드가 남는 높이를 전부 먹게 한다.
  // 카드마다 있던 릴리즈 버튼이 빠진 자리를 고정 calc 로 두면 카드 아래쪽에 빈 공간이 남는다.
  const mainStyle = isUndefined(index)
    ? {
      height: "calc(100vh - 125px)",
      m: "10px 8px 0px",
      p: "10px 15px",
      background: grey[200],
      borderRadius: "10px",
      display: "flex",
      flexDirection: "column",
    }
    : {
      // 100vh 계산을 쓰지 않는다 — 실제 가용 높이는 #tab-body 가 정하고 뷰포트보다 작다(App.scss 주석).
      height: "100%",
      // 전역 border-box 가 없는 앱이다. content-box 로 두면 위아래 10px 패딩이 높이에 더해져
      // 카드가 제 칸보다 20px 커지고 맨 아래 상태 배지가 잘린다.
      boxSizing: "border-box",
      p: "10px 0",
      pr: 0.5,
      display: "flex",
      flexDirection: "column",
    };

  const chartSeries = [
    {
      id: "AX",
      field: "AX",
      text: "AX",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("AX"), barSize: 120 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "AY",
      field: "AY",
      text: "AY",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("AY"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "AZ",
      field: "AZ",
      text: "AZ",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("AZ"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "BX",
      field: "BX",
      text: "BX",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("BX"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "BY",
      field: "BY",
      text: "BY",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("BY"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "BZ",
      field: "BZ",
      text: "BZ",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("BZ"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "CX",
      field: "CX",
      text: "CX",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("CX"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "CY",
      field: "CY",
      text: "CY",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("CY"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    },
    {
      id: "CZ",
      field: "CZ",
      text: "CZ",
      type: "bar",
      typeOptions: { stackId: "STACK_BAR", fill: ColorUtil.abcXyzTileBg("CZ"), barSize: 80 },
      orientation: "left",
      groupName: t("amt")
    }
  ];

  // 릴리즈 버튼은 화면 상단 공통 바로 빠졌다. 카드에는 상태만 남는다.
  // "릴리즈 중"도 상단 바가 보여 주므로 카드에서는 그리지 않는다.
  const ReleaseStatus = ({ planVersion }) => {
    if (isEqual(planVersion?.statusCd, RELEASED)) {
       return(
            <AppCard sx={{ display: "flex", width: "100%", height: "40px", minHeight: "40px", p: 0, mt: 1 }}>
              <CardMedia
                  sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: green[700] }}
              >
                <Typography variant="subtitle1" pl="10px" color="#fff">
                  {`${t("released")}`}
                </Typography>
              </CardMedia>
              <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                <Typography
                    variant="subtitle1"
                    component="span"
                    ml="auto"
                    width="50%"
                    textAlign="right"
                    pr="10px"
                >
                  {`${dateUtils.formatDate(planVersion?.releaseTs, "yyyy-MM-dd")}`}
                </Typography>
              </Box>
            </AppCard>
        )
      } else if (isUnconfirmedScenarioVersion(planVersion)) {
        // 확정 전 시나리오 플래닝 버전은 릴리즈할 수 없다. 확정 화면으로 보낸다.
        return <UnconfirmedScenarioNotice moduleCd="IP" height="45px" />;
      }
      return null;
    }

  useEffect(() => {
    if (!isEmpty(scenarioResultDataOrigin)) {
      settingGridDatas(scenarioResultDataOrigin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioResultDataOrigin])

  useEffect(() => {
    switch(headerCodeNumber) {
      case 0: setGridResultData(scenarioResultDataOrigin); break;
      case 1: setGridResultData(abcResultData.current); break;
      case 2: setGridResultData(xyzResultData.current); break;
      default: setGridResultData(scenarioResultDataOrigin); break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerCodeNumber]);

  const TitleCom = useCallback(({ index, name, version, updateDelFlg, isReleasing }) => {
    return (
      <Box sx={{ display: "flex", height: "55px", flexShrink: 0, alignItems: "flex-end", justifyContent: "center", borderRadius: "5px" }}>
        <LeaderboardIcon fontSize={"large"} sx={{ mb: 0.2, color: index === 0 ? green[600] : red[500] }} />
        <Typography variant="subtitle1" sx={{ pl: 1, textAlign: "center", fontWeight: "500" }}>
          {name}
        </Typography>
        {/* top 을 주지 않으면 이 절대배치 박스가 제목 행(alignItems: flex-end) 바닥에 붙어
            차트 y축 맨 위 눈금값과 딱 맞닿는다. 카드 좌상단 모서리로 올려 띄워 둔다. */}
        <ToggleComponent
          chartTypeToggle={chartTypeToggle}
          setChartTypeToggle={setChartTypeToggle}
          boxSx={{ position: "absolute", top: "8px", left: "8px", height: "auto" }} />
        {
          isEqual(version?.statusCd, RELEASED) ? null : (
            <Box sx={{ position: "absolute", top: "37px", right: "15px" }}>
              {version?.delFlg ?
                <UnlockButton
                  label="lock"
                  disabled={isEqual(version?.statusCd, RELEASING) || isReleasing}
                  onClick={() => updateDelFlg(version)} />
                : <LockButton
                  label="unlock"
                  disabled={isEqual(version?.statusCd, RELEASING) || isReleasing}
                  onClick={() => updateDelFlg(version)}
                />}
            </Box>
          )
        }
      </Box>
    );

  }, [chartTypeToggle]);

  const getContextMenuItems = (params) => {
    return params.defaultItems.filter(
      (item) => item !== 'export'
    );
  };

  return (
    <Box sx={mainStyle}>
      <TitleCom
        index={index}
        name={titleName}
        version={planVersion}
        isReleasing={isReleasing}
        updateDelFlg={updateVersionDelFlg}
      />
      <Box id={"ip-comparison-dashboard-chart"} sx={{ width: "100%", height: "calc(45% - 100px)", flexShrink: 0 }}>
        <ChartComposed
          xAxis={"classCd"}
          dataSeries={chartSeries}
          data={chartDatas}
          options={{ minHeight: 0, minWidth: "33%", xAxisAngle: 0, xAxisHeight: 60, xAxisInterval: null, mode: "fill", yAxisDomain: [0, dataMax], isAmt: true }}
          tooltipAnimation={false}
          showConfigurationPopup={false}
        />
      </Box>
      <Box sx={{ height: "90px", flexShrink: 0 }}>
        <Box sx={{ width: "100%", height: "40px" }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia
              sx={{ display: "flex", alignItems: "center", width: "50%", minWidth: "200px", bgcolor: "#048BA8" }}
            >
              <Typography variant="subtitle1" pl="10px" color="#fff">
                {t("targetInvAmt")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px"
              >
                {setCurrency(totalInvAmt)}
              </Typography>
            </Box>
          </AppCard>
        </Box>
        <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia
              sx={{ display: "flex", alignItems: "center", minWidth: "200px", width: "50%", bgcolor: "#048BA8" }}
            >
              <Typography variant="subtitle1" pl="10px" color="#fff">
                {t("invHoldingCost")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px"
              >
                {setCurrency(totalHoldingCost)}
              </Typography>
            </Box>
          </AppCard>
        </Box>
      </Box>
      <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 0, mt: 1 }}>
        <AgGridReact
          rowData={gridResultData}
          columnDefs={GridUtils.getColumnDefs(gridDefs)}
          {...DefaultGridSetting({ title })}
          defaultColDef={{
            flex: 1,
            minWidth: 50,
            suppressHeaderMenuButton: true,
            resizable: true
          }}
          getContextMenuItems={getContextMenuItems}
        />
      </Box>
      <ReleaseStatus planVersion={planVersion}/>
    </Box>
  );
}

const ToggleComponent = ({ chartTypeToggle, setChartTypeToggle, boxSx }) => {
  const buttonSx = { width: "30px", height: "30px" };

  const toggleHandler = (_, newAlignment) => {
    if (newAlignment) {
      setChartTypeToggle(newAlignment);
    }
  }

  return (
    <Box sx={{ ...boxSx }}>
      <ToggleButtonGroup
        size="small"
        value={chartTypeToggle}
        exclusive
        onChange={toggleHandler}
        aria-label="text alignment">
        <StyledToggleButton sx={{...buttonSx}} value={ABC} aria-label={ABC}>
          {ABC.toUpperCase()}
        </StyledToggleButton>
        <StyledToggleButton sx={{...buttonSx}} value={XYZ} aria-label={XYZ}>
          {XYZ.toUpperCase()}
        </StyledToggleButton>
      </ToggleButtonGroup>
    </Box>
  )
}

export default withTranslation()(ISPComparisonDashboard);
